import { formatDistanceToNow } from 'date-fns';
import { upperFirst } from 'lodash';
import pluralize from 'pluralize';

import { type Tasks } from '@air/api';
import { type Board, ClipSource } from '@air/api/types';
import { FileStatus } from '@air/feature-file-status/constants';
import { type FileTrackingItem } from '@air/feature-file-status/ui';
import { UploadStatus } from '@air/redux-uploader';
import { type Subscription, type Task } from '@air/types';
import { formatBytes, parseAirDateToISO } from '@air/utilities';

import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';
import { ImportItemIcon } from '~/components/Import/ImportItemIcon';
import { type LocationBoard } from '~/store/router/types';
import { type ImporterTask } from '~/store/tasks/types';

type ImporterType = Awaited<ReturnType<typeof Tasks.listTasks<string, 'Importer'>>>['items'][number];

export type ImporterTaskEnhanced = ImporterType & {
  metadata: ImporterTask['metadata'];
};

export type NormalizedImportItem = {
  id: string;
  status: Task['status'];
  source: ImporterTaskEnhanced['args']['source'];
  board?: Pick<Board, 'id' | 'title' | 'thumbnails'>;
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string;
  storageExceeded?: boolean;
  filesImportedCount: number;
  foldersImported: number;
  fileImportError: number;
  folderImportError: number;
  totalFilesFound: number;
  totalFoldersFound: number;
  importSizeEstimate?: number;
  importedSize: number;
  logMessage?: string;
  canCancel: boolean;
};

export const normalizeRemoteImport = (item: ImporterTaskEnhanced): NormalizedImportItem => ({
  id: item.id,
  status: item.status,
  source: item.args.source,
  board: item.metadata.board,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  storageExceeded: item.data?.storageExceeded,
  filesImportedCount: item.data?.filesImportedCount ?? 0,
  foldersImported: item.data?.foldersImported ?? 0,
  fileImportError: item.data?.fileImportError ?? 0,
  folderImportError: item.data?.folderImportError ?? 0,
  totalFilesFound: item.data?.totalFilesFound ?? 0,
  totalFoldersFound: item.data?.totalFoldersFound ?? 0,
  importSizeEstimate: item.data?.importSizeEstimate,
  importedSize: item.data?.importedSize ?? 0,
  logMessage: item.data?.log?.at(-1),
  canCancel: ['active', 'pending'].includes(item.status),
});

const localToRemoteStatus = (status: ImporterTask['status']): Task['status'] => {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'in-progress':
      return 'active';
    case 'completed':
      return 'succeeded';
    case 'error':
      return 'failed';
  }
};

export const normalizeLocalImport = (item: ImporterTask): NormalizedImportItem => ({
  id: item.localTaskId,
  status: localToRemoteStatus(item.status),
  source: item.metadata.source,
  board: item.metadata.board,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  startedAt: item.startedAt,
  storageExceeded: item.metadata.storageExceeded,
  filesImportedCount: item.metadata.filesImportedCount ?? 0,
  foldersImported: item.metadata.foldersImported ?? 0,
  fileImportError: item.metadata.fileImportError ?? 0,
  folderImportError: item.metadata.folderImportError ?? 0,
  totalFilesFound: item.metadata.totalFilesFound ?? 0,
  totalFoldersFound: item.metadata.totalFoldersFound ?? 0,
  importSizeEstimate: item.metadata.importSizeEstimate,
  importedSize: item.metadata.importedSize ?? 0,
  logMessage: item.metadata.log?.at(-1),
  canCancel: item.status === 'in-progress',
});

const statusToUploadStatus = (status: Task['status'], storageExceeded?: boolean) => {
  switch (status) {
    case 'pending':
      return UploadStatus.queued;
    case 'active':
      return storageExceeded ? UploadStatus.stopped : UploadStatus.uploading;
    case 'succeeded':
      return UploadStatus.completed;
    case 'failed':
    case 'exception':
      return UploadStatus.failed;
    case 'cancelled':
      return UploadStatus.aborted;
  }
};

export const sourceTypeToName = (source: ImporterTaskEnhanced['args']['source']) => {
  switch (source) {
    case ClipSource.dropbox:
      return 'Dropbox';
    case ClipSource.googleDrive:
      return 'Google Drive';
    case ClipSource.box:
      return 'Box';
    case ClipSource.brandfolder:
      return 'Brandfolder';
    case ClipSource.sharePoint:
      return 'SharePoint';
    default:
      return source;
  }
};

const compactNumber = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});

const formatParts = (...parts: string[]) => parts.join(' · ');

const SHOW_PROGRESS_ITEMS_REMAINING_THRESHOLD = 50;

const getSubtitle = (item: NormalizedImportItem): string | undefined => {
  const totalImported = item.filesImportedCount + item.foldersImported;
  const totalErrors = item.fileImportError + item.folderImportError;
  const totalDiscovered = item.totalFilesFound + item.totalFoldersFound;
  const formattedErrorsCount = `${compactNumber.format(totalErrors)} ${pluralize('error', totalErrors)}`;
  const formattedSize = item.importedSize > 0 ? formatBytes(item.importedSize, { numberOfDecimals: 1 }) : null;

  switch (item.status) {
    case 'pending':
      return 'Beginning import...';
    case 'active': {
      if (item.storageExceeded) {
        return 'Stopped';
      }

      if (totalImported > 0) {
        // Show "X/Y items" progress framing when there's a meaningful backlog remaining
        const showProgress = totalDiscovered - totalImported > SHOW_PROGRESS_ITEMS_REMAINING_THRESHOLD;

        return formatParts(
          showProgress
            ? `${compactNumber.format(totalImported)}/${compactNumber.format(totalDiscovered)} ${pluralize('item', totalDiscovered)}`
            : `${compactNumber.format(totalImported)} ${pluralize('item', totalImported)} imported`,
          ...(totalErrors > 0 ? [formattedErrorsCount] : []),
          ...(formattedSize ? [formattedSize] : []),
        );
      }

      const started = item.startedAt || item.createdAt;

      return started ? `Started ${formatDistanceToNow(parseAirDateToISO(started), { addSuffix: true })}` : 'Started';
    }
    case 'succeeded': {
      const parts: string[] = [];

      if (totalImported > 0) {
        parts.push(`${compactNumber.format(totalImported)} ${pluralize('item', totalImported)}`);
      }
      if (totalErrors > 0) {
        parts.push(formattedErrorsCount);
      }
      if (formattedSize) {
        parts.push(formattedSize);
      }
      if (item.updatedAt) {
        parts.push(formatDistanceToNow(parseAirDateToISO(item.updatedAt), { addSuffix: true }));
      }

      return parts.length ? formatParts(...parts) : 'Completed';
    }
    case 'cancelled':
      return item.updatedAt
        ? formatParts('Cancelled', formatDistanceToNow(parseAirDateToISO(item.updatedAt), { addSuffix: true }))
        : 'Cancelled';
    case 'failed':
    case 'exception':
      // Error message rendered instead of subtitle for failed/exception items
      return undefined;
  }
};

const getImportErrorMessage = (item: NormalizedImportItem) => {
  if (['failed', 'exception'].includes(item.status)) {
    return item.logMessage ?? 'Failed';
  }

  return '';
};

export const convertToFileTrackingItem = ({
  item,
  onRowClick,
  subscription,
  isOutOfStorage,
  includeErrorInSummary,
}: {
  item: NormalizedImportItem;
  onRowClick: (board: LocationBoard) => void;
  subscription?: Subscription;
  isOutOfStorage?: boolean;
  includeErrorInSummary: boolean;
}): FileTrackingItem => {
  const board = item.board ?? { title: 'All media', id: '' };
  const boardName = board.title;
  const sourceName = upperFirst(sourceTypeToName(item.source));
  const title = `${sourceName} to ${boardName}`;
  const thumbnail = board.thumbnails?.[0] ?? '';

  return {
    id: item.id,
    status: statusToUploadStatus(item.status, item.storageExceeded),
    error: getImportErrorMessage(item),
    title,
    subtitle: getSubtitle(item),
    sizeInBytes: item.importSizeEstimate,
    currentBytesUploaded: item.importedSize,
    disableCancel: !item.canCancel,
    disableResume: (subscription?.plan === 'basic' || subscription?.pricingModel === 'credit') && isOutOfStorage,
    includeErrorInSummary,
    avatar: thumbnail ? (
      <BoardThumbnail
        className="mr-3 inline-block size-10 shrink-0 rounded-2 object-cover"
        board={{ thumbnails: [thumbnail], title: boardName }}
        width={40}
        height={40}
        sizes="40px"
      />
    ) : (
      <ImportItemIcon source={item.source} />
    ),
    onRowClick: () => onRowClick(board),
  };
};

export const getImportHeaderTitles = ({
  erroredCount,
  pausedCount,
  stoppedCount,
  completedCount,
  abortedCount,
  progressingCount,
}: {
  pausedCount: number;
  stoppedCount: number;
  erroredCount: number;
  completedCount: number;
  abortedCount: number;
  progressingCount: number;
}): { [key in FileStatus]: { title: string; subtitle?: string } } => ({
  [FileStatus.errored]: {
    title: `Import incomplete`,
    subtitle: `${pluralize('Import', erroredCount, true)} failed`,
  },
  [FileStatus.stopped]: {
    title: `${pluralize('Import', stoppedCount, true)} stopped`,
  },
  [FileStatus.paused]: {
    title: `${pluralize('Import', pausedCount, true)} paused`,
  },
  [FileStatus.aborted]: {
    title: `${pluralize('Import', abortedCount, true)} cancelled`,
  },
  [FileStatus.progressing]: {
    title: `${pluralize('Import', progressingCount, true)} in progress`,
  },
  [FileStatus.completed]: {
    title: `${pluralize('Import', completedCount, true)} completed`,
  },
  [FileStatus.preparing]: {
    title: `Preparing`,
    subtitle: `Preparing to import`,
  },
});
